// 🐻 "A mysterious bear chipped in" (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §3.6, Nina 09-29:
// "After recharge/fund add a 'mysterious bear' or whatever chipped in to keep
// '{SPACE NAME}' on! Notification at the top.").
//
// v1: Nina fires it by hand from the backend's admin op (handler.js `adminChipIn`)
// after a real payment; the room's sockets get { type: 'chip_in', id, animal } and
// the page shows ChipInToast for 8s. Page-only: residents never see money, and
// nothing here writes anything.
//
// ⚠️ PARITY: CHIP_IN_ANIMALS mirrors justsomeguy-backend/src/ChatGame/chipIn.js
// (the server picks the animal; this list only turns its key into words + an
// emoji). chipIn.test.js requires the backend module and fails on any drift.
// Always "a mysterious <animal>", lowercase, never joined into a visitor-style
// name ("MysteriousBear" is a real possible seat name), never an amount.

export const CHIP_IN_ANIMALS = Object.freeze([
  Object.freeze({ key: 'bear', emoji: '🐻' }),
  Object.freeze({ key: 'otter', emoji: '🦦' }),
  Object.freeze({ key: 'fox', emoji: '🦊' }),
  Object.freeze({ key: 'owl', emoji: '🦉' }),
  Object.freeze({ key: 'sloth', emoji: '🦥' }),
  Object.freeze({ key: 'frog', emoji: '🐸' }),
  Object.freeze({ key: 'hedgehog', emoji: '🦔' }),
  Object.freeze({ key: 'penguin', emoji: '🐧' }),
  Object.freeze({ key: 'panda', emoji: '🐼' }),
  Object.freeze({ key: 'bunny', emoji: '🐰' }),
  Object.freeze({ key: 'duckling', emoji: '🐤' }),
  Object.freeze({ key: 'turtle', emoji: '🐢' }),
]);

export const CHIP_IN_TOAST_MS = 8000;
const QUEUE_MAX = 3; // one toast at a time; a burst keeps at most three waiting
const BY_KEY = new Map(CHIP_IN_ANIMALS.map((a) => [a.key, a]));

/** The animal for a key; an unknown key (a newer backend) falls back to the bear. */
export function chipInAnimal(key) {
  return BY_KEY.get(String(key || '').toLowerCase()) || BY_KEY.get('bear');
}

const cleanRoom = (name) => String(name == null ? '' : name).replace(/\s+/g, ' ').trim().slice(0, 60);

/** "🐻 A mysterious bear chipped in. The AI Dorm stays on." */
export function chipInLine(animalKey, roomName) {
  const a = chipInAnimal(animalKey);
  const room = cleanRoom(roomName);
  return {
    emoji: a.emoji,
    text: `A mysterious ${a.key} chipped in. ${room || 'This room'} stays on.`,
  };
}

/**
 * A socket message → a toast item, or null. Only { type: 'chip_in' } with a
 * string id counts; nothing else in the payload is read (no name, no amount).
 */
export function chipInFromSocket(msg) {
  if (!msg || typeof msg !== 'object' || msg.type !== 'chip_in') return null;
  const id = typeof msg.id === 'string' && msg.id ? msg.id.slice(0, 80) : null;
  if (!id) return null;
  return { id, animal: chipInAnimal(msg.animal).key };
}

/**
 * The page's queue (pure): add an item unless it is already showing / waiting
 * (a socket can deliver twice), keep at most three. The head is the one on screen.
 * `seen` (optional, a Set the page keeps in a ref) also drops ids already shown
 * and dismissed; this adds the id to it.
 */
export function pushChipIn(queue, item, seen = null) {
  const q = Array.isArray(queue) ? queue : [];
  if (!item || !item.id || q.some((x) => x.id === item.id)) return q;
  if (seen && typeof seen.has === 'function' && seen.has(item.id)) return q;
  if (q.length >= QUEUE_MAX) return q;
  if (seen && typeof seen.add === 'function') seen.add(item.id);
  return [...q, item];
}

/** Drop the head (its 8s ran out, or ✕). */
export function shiftChipIn(queue) {
  return Array.isArray(queue) && queue.length ? queue.slice(1) : [];
}

// 💳 10-28 Chip in, real money (STRIPE_TIPS_LANE_2026_10_28.md; Nina: the
// button sits "Beside the battery", in "Every room"): every support link the
// page opens carries the ROOM as Stripe's client_reference_id, so the webhook
// knows whose backup the tip fills and which room gets the thank-you.
// ⚠️ PARITY: chipInUrl mirrors justsomeguy-backend/src/services/StripeTips.js
// chipInUrl exactly; chipIn.test.js requires the backend module and checks a
// table of inputs against both.
export const CHIP_IN_LABEL = 'Chip in';
const ROOM_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The Chip in link for one room: the support link + client_reference_id = the
 * room's id. Not a room id → the link unchanged (the tip still counts, for no
 * room). Not an https link → null.
 */
export function chipInUrl(url, gameId) {
  const u = typeof url === 'string' ? url.trim() : '';
  if (!/^https:\/\/\S+$/i.test(u)) return null;
  const id = String(gameId == null ? '' : gameId).trim().toLowerCase();
  if (!ROOM_ID_RE.test(id)) return u;
  const hash = u.indexOf('#');
  const base = hash === -1 ? u : u.slice(0, hash);
  const frag = hash === -1 ? '' : u.slice(hash);
  if (/[?&]client_reference_id=/i.test(base)) return u;
  return `${base}${base.includes('?') ? '&' : '?'}client_reference_id=${id}${frag}`;
}

/**
 * Open the Chip in link in a new tab. Call it straight from the tap or the
 * Enter (no await before it), or a popup blocker eats the tab. → true if a
 * link was opened.
 */
export function openChipIn(url) {
  if (typeof url !== 'string' || !/^https:\/\/\S+$/i.test(url)) return false;
  try {
    window.open(url, '_blank', 'noopener,noreferrer');
    // 💳 10-29: this browser tapped Chip in for the room (the "You kept the lights on" line)
    const room = chipInRoomOf(url);
    if (room) markChipInTap(room);
    return true;
  } catch (e) {
    return false;
  }
}

// 💳 10-29 the LASTING thank-you (STRIPE_TIPS_LANE_2026_10_28.md §12; Nina: "maybe there can
// also be a notification that doesn't disappear" … "on the ghost chat with the mod saying thank
// you and if there's no ghost chat it can go in the room/chat tab (ais don't see it)", and for
// ported 1:1 rooms "something else that's more personal"). The backend keeps each SENT thank-you
// as status.chip_in_thanks ([{ id, animal, at }], the newest 20, never a name or an amount) and
// the socket's chip_in event now carries `at`. Nothing here reaches an AI: these are page-only
// lines, drawn in the ghost panel (the room's host says it) or, where there is no ghost chat
// (ported 1:1 rooms, the quiet room presets), as a centered line in the main chat.
export const CHIP_IN_LINE_TYPE = 'chip_in_line';
export const CHIP_IN_GROUP_MS = 2 * 60 * 1000;   // tips this close share ONE line
export const CHIP_IN_MINE_MS = 30 * 60 * 1000;   // a tip this soon after THIS browser tapped Chip in reads "You"
const MINE_SKEW_MS = 2 * 60 * 1000;               // the page's clock vs the server's
const THANKS_MAX = 20;
const THANKS_ID_RE = /^[A-Za-z0-9_-]{1,80}$/;
const TAP_KEY = (gameId) => `chipin_tap_v1:${String(gameId || '').toLowerCase()}`;

const thanksEntry = (x) => {
  if (!x || typeof x !== 'object') return null;
  const id = typeof x.id === 'string' ? x.id : '';
  const at = typeof x.at === 'number' ? x.at : (typeof x.at === 'string' ? Date.parse(x.at) : NaN);
  if (!THANKS_ID_RE.test(id) || !Number.isFinite(at)) return null;
  return { id, animal: chipInAnimal(x.animal).key, at };
};

/** A socket `chip_in` event → a lasting entry { id, animal, at(ms) }, or null (an older backend sends no `at`). */
export function chipInThanksFromSocket(msg) {
  if (!msg || typeof msg !== 'object' || msg.type !== 'chip_in') return null;
  return thanksEntry(msg);
}

/** status.chip_in_thanks + the ones this page heard live → one clean list, deduped by id, oldest first, the newest 20. */
export function mergeChipInThanks(fromStatus, live) {
  const byId = new Map();
  for (const list of [fromStatus, live]) {
    if (!Array.isArray(list)) continue;
    for (const x of list) {
      const e = thanksEntry(x);
      if (e && !byId.has(e.id)) byId.set(e.id, e);
    }
  }
  return [...byId.values()].sort((a, b) => a.at - b.at).slice(-THANKS_MAX);
}

/** Tips within CHIP_IN_GROUP_MS of the one before share a group: [{ id, at (the first), last, animals, ids }]. */
export function groupChipInThanks(entries, gapMs = CHIP_IN_GROUP_MS) {
  const out = [];
  for (const e of Array.isArray(entries) ? entries : []) {
    const g = out[out.length - 1];
    if (g && e.at - g.last <= gapMs) {
      g.animals.push(e.animal); g.ids.push(e.id); g.last = e.at;
    } else {
      out.push({ id: e.id, at: e.at, last: e.at, animals: [e.animal], ids: [e.id] });
    }
  }
  return out;
}

const COUNT_WORDS = { 2: 'two', 3: 'three' };
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const cleanName = (s) => String(s == null ? '' : s).replace(/\s+/g, ' ').trim().slice(0, 60);
const emojisOf = (animals) => animals.slice(0, 5).map((a) => chipInAnimal(a).emoji).join('');

/**
 * One group → { text, speaker }. `kind`: 'host' (the room's host says it in the ghost panel;
 * no host → no speaker) or 'ported' (a 1:1 room: names their AI, never in the AI's voice).
 * `mine`: this browser tapped Chip in just before → the personal "You" line.
 * Never a name of whoever paid, never an amount, never an em dash.
 */
export function chipInThanksLine(group, { kind = 'host', hostName = null, roomName = null, residentName = null, mine = false } = {}) {
  const animals = group && Array.isArray(group.animals) && group.animals.length ? group.animals : ['bear'];
  const n = animals.length;
  const host = cleanName(hostName);
  const room = cleanName(roomName);
  const resident = cleanName(residentName);
  const speaker = kind === 'host' && host ? host : null;
  const who = n === 1 ? `mysterious ${chipInAnimal(animals[0]).key}` : `${COUNT_WORDS[n] || n} mysterious animals`;
  const emoji = emojisOf(animals);
  if (mine) {
    return { text: `💛 You kept the lights on for ${(kind === 'ported' && resident) || room || 'this room'}. Thank you.`, speaker };
  }
  if (kind === 'ported') {
    return { text: `💛 ${cap(n === 1 ? `a ${who}` : who)} kept the lights on for ${resident || room || 'this room'}. Thank you!`, speaker: null };
  }
  if (speaker) {
    return { text: `Thank you, ${who} ${emoji} ${room || 'This room'} stays on.`, speaker };
  }
  return { text: `${emoji} ${cap(n === 1 ? `a ${who}` : who)} chipped in. Thank you!`, speaker: null };
}

/** Remember that THIS browser tapped Chip in for a room (localStorage; never throws). */
export function markChipInTap(gameId, now = Date.now()) {
  const id = String(gameId || '').trim().toLowerCase();
  if (!ROOM_ID_RE.test(id)) return false;
  try { window.localStorage.setItem(TAP_KEY(id), String(now)); return true; } catch (e) { return false; }
}

/** When this browser last tapped Chip in for a room (ms), or null. Never throws. */
export function chipInTapAt(gameId) {
  try {
    const v = Number(window.localStorage.getItem(TAP_KEY(gameId)));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch (e) {
    return null;
  }
}

/** A Chip in link's room (its client_reference_id), or null. */
export function chipInRoomOf(href) {
  const m = /[?&]client_reference_id=([0-9a-f-]{36})(?:[&#]|$)/i.exec(String(href || ''));
  return m && ROOM_ID_RE.test(m[1]) ? m[1].toLowerCase() : null;
}

/** The ONE group that is "mine": the first whose tips land within CHIP_IN_MINE_MS after the tap. */
export function mineGroupId(groups, tapAt) {
  if (!Number.isFinite(tapAt)) return null;
  for (const g of Array.isArray(groups) ? groups : []) {
    if (g.last >= tapAt - MINE_SKEW_MS && g.at <= tapAt + CHIP_IN_MINE_MS) return g.id;
  }
  return null;
}

/**
 * The page's lasting lines. → { ghost: [ghost-panel messages], feed: [main-chat items] }.
 * `place`: 'ghost' (the host line in the ghost panel; a Live room's inline crowd draws the same
 * list) or 'feed' (a centered line in the main chat). Ported rooms always use their own words.
 */
export function chipInThanksView(entries, { place = 'ghost', isPorted = false, hostName = null, hostEmoji = null, roomName = null, residentName = null, tapAt = null } = {}) {
  const groups = groupChipInThanks(entries);
  if (!groups.length) return { ghost: [], feed: [] };
  const mine = mineGroupId(groups, tapAt);
  const kind = isPorted ? 'ported' : 'host';
  const lines = groups.map((g) => ({ g, line: chipInThanksLine(g, { kind, hostName, roomName, residentName, mine: g.id === mine }), mine: g.id === mine }));
  if (place === 'feed') {
    return {
      ghost: [],
      feed: lines.map(({ g, line, mine: m }, i) => ({
        id: `chipin_${g.id}`,
        type: CHIP_IN_LINE_TYPE,
        created_at: new Date(g.at).toISOString(),
        _ts: g.at,
        _seq: i,
        data: { text: line.text, speaker: line.speaker, mine: m },
      })),
    };
  }
  return {
    ghost: lines.map(({ g, line }) => (line.speaker
      ? { id: `chipin_${g.id}`, sender: line.speaker, sender_id: 'moderator', message: line.text, timestamp: g.at, is_moderator: true, moderator_emoji: hostEmoji || '💛', is_chip_in_thanks: true }
      : { id: `chipin_${g.id}`, sender: '', sender_id: 'system', message: line.text, timestamp: g.at, is_chip_in_thanks: true })),
    feed: [],
  };
}

/**
 * Slot the ghost-panel lines into the panel's list by time (after the last message at or
 * before each). A line older than every loaded message waits for "load older" (the panel
 * pages); an empty panel shows them all. Same array back when there is nothing to add.
 */
export function mergeChipInIntoGhost(messages, extra) {
  const list = Array.isArray(messages) ? messages : [];
  if (!Array.isArray(extra) || !extra.length) return list;
  if (!list.length) return extra.slice();
  const tsOf = (m) => {
    const v = m && m.timestamp;
    const t = typeof v === 'number' ? v : Date.parse(v || (m && m.created_at) || '');
    return Number.isFinite(t) ? t : NaN;
  };
  const ts = list.map(tsOf);
  let oldest = Infinity;
  for (const t of ts) if (Number.isFinite(t) && t < oldest) oldest = t;
  const slots = new Map();
  let placed = 0;
  for (const x of extra) {
    if (list.some((m) => m && m.id === x.id)) continue;
    if (!(x.timestamp >= oldest)) continue;
    let p = -1;
    for (let i = 0; i < list.length; i++) if (Number.isFinite(ts[i]) && ts[i] <= x.timestamp) p = i;
    if (p === -1) continue;
    if (!slots.has(p)) slots.set(p, []);
    slots.get(p).push(x);
    placed++;
  }
  if (!placed) return list;
  const out = [];
  list.forEach((m, i) => { out.push(m); const here = slots.get(i); if (here) out.push(...here); });
  return out;
}
